<template lang="pug">
div.document-preview-pane
  div.dpp-loading-overlay(v-if="isNavigating")
    v-progress-circular(indeterminate color="#18bedf" size="32")
  div.dpp-content-area
    template(v-if="isImageFile")
      div.dpp-image-loading(v-if="imageLoading")
        v-progress-circular(indeterminate color="#18bedf" size="32")
      img.dpp-image(
        v-else-if="resolvedImageUrl"
        :src="resolvedImageUrl"
        :alt="fileExtension"
        @error="onImageError"
      )
      div.dpp-unsupported-msg(v-else-if="imageSasFetchError") {{ $t('DOCUMENT_AGENT.PREVIEW_FETCH_ERROR') }}
      div.dpp-unsupported-msg.dpp-image-error(v-else-if="imageLoadError")
        v-icon(size="48" color="grey lighten-1") mdi-image-broken-variant
        span {{ $t('DOCUMENT_AGENT.FILE_LOAD_ERROR') }}
    template(v-else-if="isPdfFile")
      div.dpp-pdf-loading(v-if="pdfLoading")
        v-progress-circular(indeterminate color="#18bedf" size="32")
      iframe.dpp-pdf-iframe(
        v-else-if="resolvedPdfUrl"
        :key="resolvedPdfUrl"
        :src="resolvedPdfUrl"
        frameborder="0"
        @error="onPdfError"
      )
      div.dpp-unsupported-msg(v-else-if="pdfSasFetchError") {{ $t('DOCUMENT_AGENT.PREVIEW_FETCH_ERROR') }}
      div.dpp-unsupported-msg(v-else-if="pdfLoadError") {{ $t('DOCUMENT_AGENT.PREVIEW_LOAD_ERROR') }}
    template(v-else-if="isWordOrPptFile")
      div.dpp-word-loading(v-if="wordPptLoading")
        v-progress-circular(indeterminate color="#18bedf" size="32")
      div.dpp-unsupported-msg(v-else-if="wordPptLoadError") {{ $t('DOCUMENT_AGENT.EXTRACT_TEXT_ERROR') }}
      div.dpp-unsupported-msg(v-else-if="wordPptText === ''") {{ $t('DOCUMENT_AGENT.NO_EXTRACTED_TEXT') }}
      div.dpp-word-content(v-else-if="wordPptText" v-html="renderedWordPptHtml")
    template(v-else-if="isSupportedFileType")
      slot
    div.dpp-unsupported-msg(v-else) {{ $t('DOCUMENT_AGENT.FILE_TYPE_NOT_SUPPORTED') }}
</template>

<script>
import UpGPTPortalDataServices from '@/services/UpGPTPortalDataServices';
import MarkdownIt from 'markdown-it';
import markdownItAnchor from 'markdown-it-anchor';
import markdownItTocDoneRight from 'markdown-it-toc-done-right';

export default {
  name: 'DocumentPreviewPane',

  inject: ['sasUrlCache', 'fetchAndCacheSasUrl'],

  props: {
    column_name:   { type: String, required: true },
    rowObjectId:   { type: [String, Number], required: true },
    fileUrl:       { type: String, required: true },
    fileExtension: { type: String, required: true },
    columnApiName: { type: String, default: '' },
  },

  data() {
    return {
      activeColumnName: this.column_name,
      isNavigating: false,
      switchResolve: null,
      imageLoading: false,
      resolvedImageUrl: '',
      imageSasFetchError: false,
      imageLoadError: false,
      pdfLoading: false,
      resolvedPdfUrl: '',
      pdfSasFetchError: false,
      pdfLoadError: false,
      mdInstance: null,
      wordPptLoading: false,
      wordPptText: null,
      wordPptLoadError: false,
    };
  },

  computed: {
    isSupportedFileType() {
      const supported = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'webp', 'docx', 'doc', 'pptx', 'ppt'];
      return supported.includes((this.fileExtension || '').toLowerCase());
    },
    isImageFile() {
      const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
      return imageExts.includes((this.fileExtension || '').toLowerCase());
    },
    isPdfFile() {
      return (this.fileExtension || '').toLowerCase() === 'pdf';
    },
    isWordOrPptFile() {
      return ['docx', 'doc', 'pptx', 'ppt'].includes((this.fileExtension || '').toLowerCase());
    },
    renderedWordPptHtml() {
      if (!this.wordPptText || !this.mdInstance) return '';
      return this.mdInstance.render(this.wordPptText);
    },
  },

  watch: {
    column_name(newVal) {
      this.activeColumnName = newVal;
      // 若有待處理的欄位切換 Promise，通知其完成
      if (this.switchResolve) {
        const resolve = this.switchResolve;
        this.switchResolve = null;
        resolve();
      }
    },
    fileUrl(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.isImageFile) this.loadImage();
        else if (this.isPdfFile) this.loadPdf();
        else if (this.isWordOrPptFile) this.loadWordPptText();
      }
    },
    fileExtension(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (this.isImageFile) this.loadImage();
        else if (this.isPdfFile) this.loadPdf();
        else if (this.isWordOrPptFile) this.loadWordPptText();
      }
    },
  },

  mounted() {
    this.mdInstance = new MarkdownIt({ html: true, breaks: true, linkify: true });
    this.mdInstance.enable(['table']);
    this.mdInstance.use(markdownItAnchor);
    this.mdInstance.use(markdownItTocDoneRight);
    if (this.isImageFile) {
      this.loadImage();
    } else if (this.isPdfFile) {
      this.loadPdf();
    } else if (this.isWordOrPptFile) {
      this.loadWordPptText();
    }
  },

  methods: {
    /**
     * 供 Feature 1 透過 $refs 呼叫。
     * 由 Feature 3 引用來源點擊後，Feature 1 轉呼叫此方法，
     * 指示左側窗格切換欄位並跳頁定位。
     *
     * @param {Object} payload
     * @param {string} payload.column_name  - 目標欄位識別（flexfield_column）
     * @param {number} [payload.page_num]   - PDF 目標頁碼（1-based）
     * @param {number} [payload.image_num]  - 圖片索引（用於視覺標示）
     */
    async navigateTo({ column_name, page_num, image_num } = {}) {
      if (!column_name) return;
      if (!this.isSupportedFileType && column_name === this.activeColumnName) return;

      // 1. 若目標欄位與目前顯示欄位不同，請求 Feature 1 切換
      if (column_name !== this.activeColumnName) {
        this.isNavigating = true;
        try {
          await this._requestSwitchColumn(column_name);
          // 等待 fileUrl watcher 觸發的 loadPdf/loadImage 完成
          if (this.pdfLoading || this.imageLoading || this.wordPptLoading) {
            await new Promise(resolve => {
              const unwatch = this.$watch(
                () => !this.pdfLoading && !this.imageLoading && !this.wordPptLoading,
                ready => { if (ready) { unwatch(); resolve(); } }
              );
            });
          }
        } catch {
          // 目標欄位不存在或無檔案，Feature 1 不會更新 props → 逾時後放棄
          this.isNavigating = false;
          return;
        }
        this.isNavigating = false;
      }

      // 2. 等待載入流程完成（由 loadImage / loadPdf 建立的 Promise）
      if (this._loadPromise) {
        await this._loadPromise;
      }

      // 3. 若 SAS URL 取得失敗，錯誤訊息已顯示，中止頁內定位
      if (this.imageSasFetchError || this.pdfSasFetchError) {
        return;
      }

      // 4. 根據檔案類型執行頁內定位
      const ext = this.fileExtension ? this.fileExtension.toLowerCase() : '';

      if (ext === 'pdf' && page_num != null) {
        this._jumpPdfToPage(page_num);
      } else if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext) && image_num != null) {
        this.$emit('navigate-to-image', { image_num });
      }
      // Word / PowerPoint 或無位置資訊：僅完成欄位切換，不執行頁內定位
    },

    /**
     * 向 Feature 1 發出切換欄位請求，並等待 column_name prop 更新。
     * 若 Feature 1 判斷目標欄位不存在或無檔案，prop 不會更新，
     * 3 秒後自動 reject（視為「不執行任何動作」）。
     */
    /**
     * 取得當前欄位檔案的 SAS URL。
     * 以 `${rowObjectId}:${column_name}` 為 key 查快取，
     * 未命中時呼叫 Feature 1 的 fetchAndCacheSasUrl 寫入快取後回傳。
     * 供子元件（PDF 檢視器、圖片檢視器等）按需呼叫。
     *
     * @param {string} blobUrl - Azure Blob 路徑
     * @returns {Promise<string|null>} SAS URL 或 null
     */
    async resolveSasUrl(blobUrl) {
      return this.fetchAndCacheSasUrl(this.rowObjectId, this.column_name, blobUrl);
    },

    async loadImage() {
      if (!this.fileUrl) return;
      this.imageLoading = true;
      this.resolvedImageUrl = '';
      this.imageSasFetchError = false;
      this.imageLoadError = false;
      this._loadPromise = new Promise((resolve) => {
        this._loadResolve = resolve;
      });
      try {
        if (/^https?:\/\//i.test(this.fileUrl)) {
          this.resolvedImageUrl = this.fileUrl;
        } else {
          const sasUrl = await this.resolveSasUrl(this.fileUrl);
          if (!sasUrl) {
            this.imageSasFetchError = true;
          } else {
            this.resolvedImageUrl = sasUrl;
          }
        }
      } catch (err) {
        console.error('[DPP_IMAGE_LOAD_ERROR]', err);
        this.imageSasFetchError = true;
      } finally {
        this.imageLoading = false;
        if (this._loadResolve) {
          this._loadResolve();
          this._loadResolve = null;
        }
      }
    },
    onImageError() {
      this.imageLoadError = true;
      this.resolvedImageUrl = '';
    },
    onPdfError() {
      this.pdfLoadError = true;
      this.resolvedPdfUrl = '';
    },

    _isSharePointUrl(url) {
      return /sharepoint\.com|1drv\.ms/i.test(url);
    },

    /**
     * 探測 URL 是否可及。
     * 對 SAS URL（Azure Blob）優先使用 cors mode 以判斷 HTTP 狀態碼；
     * 若 cors 失敗（CORS 不支援），fallback 至 no-cors（僅偵測網路層錯誤）。
     * @param {string} url
     * @returns {Promise<boolean>}
     */
    async _probeUrl(url) {
      // 先嘗試 cors mode，可偵測 HTTP 狀態碼（如 403、404）
      try {
        const res = await fetch(url, { method: 'HEAD', mode: 'cors' });
        return res.ok;
      } catch {
        // cors 失敗時 fallback 至 no-cors（opaque response，僅偵測網路層錯誤）
        try {
          await fetch(url, { method: 'HEAD', mode: 'no-cors' });
          return true;
        } catch {
          return false;
        }
      }
    },

    async loadPdf() {
      if (!this.fileUrl) return;
      this.pdfLoading = true;
      if (this._pdfBlobUrl) {
        URL.revokeObjectURL(this._pdfBlobUrl);
        this._pdfBlobUrl = null;
      }
      this.resolvedPdfUrl = '';
      this.pdfSasFetchError = false;
      this.pdfLoadError = false;
      this._loadPromise = new Promise((resolve) => {
        this._loadResolve = resolve;
      });
      try {
        if (this._isSharePointUrl(this.fileUrl)) {
          const res = await UpGPTPortalDataServices.getOnedriveFile(
            this.rowObjectId, this.columnApiName
          );
          const blobUrl = URL.createObjectURL(res.data);
          this._pdfBlobUrl = blobUrl;
          this.resolvedPdfUrl = `${blobUrl}#view=FitH`;
        } else if (/^https?:\/\//i.test(this.fileUrl)) {
          const reachable = await this._probeUrl(this.fileUrl);
          if (!reachable) {
            this.pdfLoadError = true;
            return;
          }
          this.resolvedPdfUrl = `${this.fileUrl}#view=FitH`;
        } else {
          let sasUrl;
          try {
            const res = await UpGPTPortalDataServices.getSasUrlInline(this.fileUrl);
            sasUrl = res.data?.sas_url;
          } catch (sasErr) {
            console.error('[DPP_PDF_SAS_FETCH_ERROR]', sasErr);
            this.pdfSasFetchError = true;
            return;
          }
          if (!sasUrl) {
            this.pdfSasFetchError = true;
            return;
          }
          const reachable = await this._probeUrl(sasUrl);
          if (!reachable) {
            this.pdfLoadError = true;
            return;
          }
          this.resolvedPdfUrl = `${sasUrl}#view=FitH`;
        }
      } catch (err) {
        console.error('[DPP_PDF_LOAD_ERROR]', err);
        this.pdfLoadError = true;
      } finally {
        this.pdfLoading = false;
        if (this._loadResolve) {
          this._loadResolve();
          this._loadResolve = null;
        }
      }
    },

    /**
     * 更新 PDF iframe src 至指定頁碼。
     * 透過在 URL 末尾附加 #page=N 實現跳轉。
     * 若目標 URL 與當前相同（連續點擊同一頁），先清空再重設以強制 iframe 重新載入。
     *
     * @param {number} pageNum - 目標頁碼（1-based）
     */
    _jumpPdfToPage(pageNum) {
      const base = this.resolvedPdfUrl.split('#')[0];
      if (!base) return;
      const target = `${base}#page=${pageNum}&view=FitH`;
      this.resolvedPdfUrl = '';
      // double rAF：確保空白態被 paint 一個完整 frame 後才建立新 iframe，
      // 讓 Chrome PDF viewer 從完全乾淨的狀態讀取 #page=N
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.resolvedPdfUrl = target;
        });
      });
    },

    _requestSwitchColumn(targetColumnName) {
      return new Promise((resolve, reject) => {
        this.switchResolve = resolve;
        this.$emit('request-switch-column', { column_name: targetColumnName });

        // 逾時保護：3 秒內若 Feature 1 未更新 column_name prop，視為目標不存在
        setTimeout(() => {
          if (this.switchResolve) {
            this.switchResolve = null;
            reject(new Error('switch-column timeout'));
          }
        }, 3000);
      });
    },

    async loadWordPptText() {
      this.wordPptLoading = true;
      this.wordPptText = null;
      this.wordPptLoadError = false;
      try {
        const res = await UpGPTPortalDataServices.documentAgentExtractedText(
          this.rowObjectId,
          this.columnApiName,
        );
        const raw = res.data?.text ?? '';
        // HTML block（如 <table>）直到空行才結束，若標題前後缺少空行會被吞掉
        // 確保每個 Markdown 標題（# ~ ######）前後都有空行
        this.wordPptText = raw
          .replace(/([^\n])\n(#{1,6} )/g, '$1\n\n$2')
          .replace(/(#{1,6} [^\n]+)\n([^\n])/g, '$1\n\n$2');
      } catch (err) {
        console.error('[DPP_WORD_PPT_LOAD_ERROR]', err);
        this.wordPptLoadError = true;
      } finally {
        this.wordPptLoading = false;
      }
    },
  },

  beforeDestroy() {
    if (this._pdfBlobUrl) {
      URL.revokeObjectURL(this._pdfBlobUrl);
    }
  },
};
</script>

<style scoped>
.document-preview-pane {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.dpp-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
}

.dpp-content-area {
  flex: 1 1 0;
  min-height: 0;
  overflow: auto;
}

.dpp-unsupported-msg {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999999;
  font-size: 14px;
}

.dpp-image-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.dpp-image {
  display: block;
  width: 100%;
  height: auto;
}

.dpp-image-error {
  flex-direction: column;
  gap: 8px;
}

.dpp-pdf-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.dpp-pdf-iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
}

.dpp-word-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
.dpp-word-content {
  padding: 16px;
  font-size: 14px;
  line-height: 1.6;
  overflow-y: auto;
  height: 100%;
  box-sizing: border-box;
}
.dpp-word-content ::v-deep table {
  border-collapse: collapse;
  width: 100%;
  margin: 8px 0;
}
.dpp-word-content ::v-deep th,
.dpp-word-content ::v-deep td {
  border: 1px solid #ddd;
  padding: 6px 12px;
  text-align: left;
}
.dpp-word-content ::v-deep th {
  background-color: #f5f5f5;
  font-weight: 600;
}
</style>
